/** 출고일자 조회 범위(3장 6절). 1.0 도 `moment().subtract(30,'d')` ~ 오늘로 고정하고 입력을 잠갔다. 화면에 노출하지 않는다. */
export const ACT_DATE_RANGE_DAYS = 30

/** 마스터 조회여부 셀렉트 — 셸 `MASTERFLAGSELECT_OPTIONS` 표기와 동일. API `flag` 로 환산한다. */
export const MASTER_FLAG_OPTIONS = [
  { label: 'N (원송장 주문목록 기준)', value: 'N' },
  { label: 'Y (상품 마스터 기준)', value: 'Y' },
] as const

export type MasterFlagValue = (typeof MASTER_FLAG_OPTIONS)[number]['value']

/** D3 — 세션에 유지하는 값. 스캔 입력·반품번호·모달 필터·편집 중인 행은 유지하지 않는다. */
export interface ReturnWorkFilters {
  masterFlag: MasterFlagValue
  isSendToDoosonERP: boolean
}

export const DEFAULT_FILTERS: ReturnWorkFilters = {
  masterFlag: 'N',
  isSendToDoosonERP: false,
}

/**
 * 검수 녹화 기본값(7단계). autoRecord 는 **카메라가 준비되자마자** 녹화하는 훅 옵션이라 끈다 — 2.0 자동녹화(PD 결정 2026-09-30)는
 * 진입 즉시가 아니라 검수가 시작될 때(송장 정보 채움·수기입력 켬) 엔트리(index.tsx)가 시작한다. 녹화 버튼은 없다.
 */
export const INSPECTION_DEFAULTS = {
  resolution: '1280 × 720',
  autoRecord: false,
  /** 0 = 제한 없음(1.0 기본값과 동일). */
  recordTimeLimitSeconds: 0,
} as const

/** 주문검색 기본 조회 기간(개월) — 출고일자를 비워 두고 [조회]하면 오늘 기준 이 기간으로 찾는다(BE 는 기간이 필수다). 옛 모달 기본값(1개월)과 같다. */
export const ORDER_SEARCH_PERIOD_MONTHS = 1

export const MESSAGES = {
  itemScanRequired: '반송장과 원송장을 먼저 스캔해 주세요.',
  /**
   * HEAD 조회를 못 하는 네 가지 이유(LOGISPRD-12611). 예전에는 넷 다 조용히 return 해서, 값을 넣고
   * Enter 를 쳐도 빈 값으로 쳤을 때와 구분되지 않았다. rgr18 이 같은 자리에서 무결과를 문장으로
   * 알려주는 수준에 맞춘다.
   */
  returnInvoiceRequired: '반송장을 먼저 스캔해 주세요.',
  originalInvoiceRequired: '원송장을 스캔하거나 주문검색으로 골라 주세요. 반송장만으로는 원출고 내역을 조회할 수 없습니다.',
  headAlreadyLoaded: '이미 원출고 내역을 조회했습니다. 검수완료 후 다시 스캔해 주세요.',
  manualEntryNoSearch: '수기입력 중에는 원출고 내역을 조회하지 않습니다.',
  headNotFound: '해당 원송장의 주문 내역을 찾을 수 없습니다.',
  itemNotFound: '해당 바코드의 주문 내역이 없습니다.',
  searchFailed: '조회 중 오류가 발생했습니다.',
  /** 데모(컨덕터) 검수완료 토스트와 같은 문구 — 데모가 기준이다. */
  saveSuccess: '검수완료 처리되었습니다',
  saveSuccessWithDuplicates: (count: number) => `저장했습니다. 중복 반품번호 ${count}건은 제외됐습니다.`,
  saveFailed: '저장에 실패했습니다.',
  noRowsToSave: '저장할 스캔 결과가 없습니다.',
  orderNumberInvalid: '반품번호는 숫자로 입력해 주세요.',
  /** [저장] 필수 칸 빈 값 — 1.0 CommonUtil.onSaveData 알럿과 같은 뜻(LOGISPRD-17253). 행 번호는 표 순서(1부터) */
  requiredMissing: (rowNo: number, title: string) => `${rowNo}행의 필수값 '${title}' 열이 입력되지 않았습니다. 확인 후 다시 시도해 주세요.`,
  shipperUnauthorized: '이 화주의 반품을 저장할 권한이 없습니다.',
  invoiceOrCommentRequired: '원송장이 없으면 수기입력사유를 입력해야 합니다.',
  uploadFailed: '검수 파일 업로드에 실패했습니다.',
  orderSearchFailed: '주문 검색 중 오류가 발생했습니다.',
  orderNotPicked: '주문을 골라 주세요.',
  /** 데모(컨덕터) 원송장 조회·주문검색 [선택] 토스트와 같은 문구. */
  invoiceLoaded: '송장 정보를 불러왔습니다.',
  /** 검수 녹화 미리보기 칸 문구(카메라 영상이 없을 때만 보인다). */
  cameraConnecting: '카메라 연결 중',
  cameraUnavailable: '카메라를 사용할 수 없습니다',
  cameraNotReady: '카메라가 연결되지 않아 촬영할 수 없습니다.',
  captureFailed: '촬영에 실패했습니다.',
} as const

/** 표 id(생성본 columnLayout 키) → gridCd — 화면관리 그리드 설정과 개인 컬럼 배치 저장이 같은 값을 쓴다(docs/conventions/grid-code.md, LOGISPRD-17253) */
export const GRID_CDS = { rgr06WorkTable: 'RGR17_HEAD' } as const
